import './i18n' // Initialize i18next before any components import useTranslation
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  ColorSchemeScript,
  type CSSVariablesResolver,
  createTheme,
  MantineProvider,
  Paper,
} from '@mantine/core'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import 'leaflet/dist/leaflet.css'
import { DatesProvider } from '@mantine/dates'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { PageLoader } from './components/PageLoader'
import { routeTree } from './routeTree.gen'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Makassar')

const theme = createTheme({
  // "Pelabuhan" design language — palette inspired by nautical charts:
  // cream paper (kertas), navy ink (tinta), deep-sea teal (laut), coral CTA (karang),
  // warm sand (pasir), sky info (langit). Primary = laut. CTA accents = karang.
  primaryColor: 'laut',
  primaryShade: { light: 6, dark: 5 },
  defaultRadius: 'md',
  fontFamily: "'Plus Jakarta Sans', system-ui, Avenir, Helvetica, Arial, sans-serif",
  headings: {
    // Fraunces (variable serif) for hero / greeting / display headings —
    // gives the "premium nautical chart" identity. Components opt-in by
    // setting fontFamily explicitly; default uses Plus Jakarta Sans so
    // table headers / form labels stay technical & readable.
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontWeight: '700',
  },
  other: {
    fontDisplay: "'Fraunces', 'Cardo', Georgia, serif",
  },
  colors: {
    // ── laut: primary teal-deep, brand interactive (links, active items) ──
    laut: [
      '#E6F5F5', // 0 — tide foam, very pale (sub backgrounds)
      '#C0E5E4', // 1
      '#94D3D1', // 2
      '#68C1BE', // 3
      '#3AAFAB', // 4
      '#1A9C98', // 5 — dark mode primary
      '#0E7C7B', // 6 — light mode primary, base
      '#0B6362', // 7
      '#084948', // 8
      '#053130', // 9 — deep sea
    ] as const,
    // ── karang: terracotta coral, CTA / urgent alert / sunset accent ──
    karang: [
      '#FFF2EC', // 0
      '#FFDDD0', // 1
      '#FFC1AB', // 2
      '#FFA585', // 3
      '#FB8B6A', // 4
      '#EE7654', // 5
      '#E07856', // 6 — base
      '#B65B3F', // 7
      '#8C422C', // 8
      '#5C2818', // 9
    ] as const,
    // ── kertas: cream paper — page background light mode, surface highlight ──
    kertas: [
      '#FFFDF8', // 0
      '#FBF7EE', // 1
      '#F8F2E7', // 2 — page bg light (body)
      '#F2EAD7', // 3
      '#EBE0C4', // 4
      '#E3D5B0', // 5
      '#D4C29A', // 6
      '#A89679', // 7
      '#7D6E58', // 8
      '#544838', // 9
    ] as const,
    // ── pasir: warm sand — hover surface, soft highlights ──
    pasir: [
      '#FAF5E9',
      '#F2E8CE',
      '#E8D5B7', // 2 — base
      '#DCC39B',
      '#CFB07F',
      '#C09C63',
      '#A98545',
      '#86692F',
      '#624C20',
      '#3F3014',
    ] as const,
    // ── langit: sky blue — info accent, secondary chart series ──
    langit: [
      '#EAF3FA',
      '#CFE2F0',
      '#B0CFE5',
      '#92BCDA',
      '#7CB4D8', // 4 — base
      '#5E9DC6',
      '#4585B3',
      '#306A95',
      '#1F4F73',
      '#11334F',
    ] as const,
    // ── tinta: deep navy ink — text headers in light mode ──
    // Kept under "navy" key for backward-compat with existing c="navy.X" refs;
    // tuned slightly cooler to match nautical chart ink (#1A3548 base = shade 9).
    navy: [
      '#EBF0F4', // 0
      '#CFDAE3', // 1
      '#A8BBCB', // 2
      '#7E9AB1', // 3
      '#5A7E97', // 4
      '#3E677F', // 5
      '#2A5063', // 6
      '#1F3E4E', // 7
      '#1A3548', // 8 — tinta base
      '#0F2434', // 9
    ] as const,
    // ── dark palette: midnight ocean — deep teal-navy body, layered surfaces ──
    // Reference: reading a nautical chart by lamplight in a captain's quarters.
    dark: [
      '#EAF1F2', // 0 — primary text (warm crisp white)
      '#C9D4D6', // 1
      '#8FA0A4', // 2 — dimmed
      '#5A6C72', // 3 — placeholder
      '#1F3942', // 4 — border (teal-tinted)
      '#172C35', // 5 — hover bg
      '#102129', // 6 — card / paper surface  ← --app-card-bg dark
      '#0A1820', // 7 — page body  ← --mantine-color-body dark
      '#060F14', // 8
      '#030709', // 9
    ] as const,
  },
  // Minimal shadows — kept ONLY for elevation context (modals, popovers,
  // hover lifts). Cards/papers rely on background contrast instead.
  shadows: {
    xs: '0 1px 2px rgba(10, 24, 32, 0.04)',
    sm: '0 2px 6px rgba(10, 24, 32, 0.06)',
    md: '0 6px 18px rgba(10, 24, 32, 0.10)',
    lg: '0 12px 32px rgba(10, 24, 32, 0.14)',
    xl: '0 20px 48px rgba(10, 24, 32, 0.20)',
  },
  components: {
    // Pelabuhan: cards float as warm-paper surfaces on the cream body.
    // Shadow uses pasir (sand) tone so it reads as depth, not a border.
    Card: Card.extend({
      defaultProps: { radius: 'lg' },
      styles: {
        root: {
          background: 'var(--app-card-bg)',
          border: 'none',
          boxShadow: 'var(--app-card-shadow)',
        },
      },
    }),
    Paper: Paper.extend({
      defaultProps: { radius: 'lg' },
      styles: {
        root: {
          background: 'var(--app-card-bg)',
          border: 'none',
          boxShadow: 'var(--app-card-shadow)',
        },
      },
    }),
    Button: Button.extend({ defaultProps: { radius: 'xl' } }),
    Badge: Badge.extend({ defaultProps: { radius: 'xl' } }),
    ActionIcon: ActionIcon.extend({ defaultProps: { radius: 'lg' } }),
  },
})

// CSSVariablesResolver — generates [data-mantine-color-scheme="light/dark"] scoped
// CSS rules, so variables update instantly on scheme switch (no stale inline styles).
const cssResolver: CSSVariablesResolver = () => ({
  variables: {
    // Display font for hero / greeting / nautical-chart headings. Components
    // use this via `style={{ fontFamily: 'var(--font-display)' }}` or the
    // helper class .pelabuhan-display in pelabuhan.css.
    '--font-display': "'Fraunces', 'Cardo', Georgia, serif",
  },
  light: {
    // Pelabuhan: warm kertas body, cards are slightly brighter kertas paper.
    // Shadow is pasir (sand) toned — depth without a hard border.
    '--mantine-color-body': '#F0E6D0', // kertas-deep — warm chart table
    '--mantine-color-default-border': '#E3D5B0', // soft sand
    '--app-card-bg': '#FDFAF3', // kertas.0 warm — "quality paper"
    '--app-card-shadow': '0 2px 8px -2px rgba(139,110,70,0.09)',
    '--app-ink': '#1A3548', // tinta — deep navy ink
    '--app-surface-paper': '#F9F4E8', // kertas.1 — secondary surface
    '--app-surface-accent': '#0E7C7B', // laut.6 — accent card bg
  },
  dark: {
    // Midnight ocean body, deep-teal card surface.
    '--app-card-bg': '#0F2530',
    '--app-card-shadow': '0 1px 3px rgba(0,0,0,0.25), 0 4px 14px -4px rgba(0,0,0,0.35)',
    '--app-ink': '#EAF1F2',
    '--app-surface-paper': '#0F2530',
    '--app-surface-accent': 'var(--mantine-color-laut-7)',
  },
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Show the branded loader for routes whose loader takes >300ms — short
  // transitions still feel instant (no flicker), longer ones get the same
  // 3×3 mark animation as the initial splash so the experience is unified.
  defaultPendingComponent: () => <PageLoader />,
  defaultPendingMs: 300,
  defaultPendingMinMs: 200,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function App() {
  return (
    <>
      <ColorSchemeScript defaultColorScheme="auto" />
      <MantineProvider theme={theme} defaultColorScheme="auto" cssVariablesResolver={cssResolver}>
        <DatesProvider settings={{ firstDayOfWeek: 1 }}>
          <ModalsProvider>
            <Notifications position="top-right" />
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </ModalsProvider>
        </DatesProvider>
      </MantineProvider>
    </>
  )
}
